/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomFormTextFieldV3.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import InfoButton from "../../../infoButton/InfoButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const CustomFormTextFieldV3 = ({
  size = "small",
  label = "",
  type = "text",
  fldType = "text",
  placeholder = "",
  required = false,
  value = "",
  setValue = () => { },
  variant = "outlined",
  valueError = "",
  setValueError = () => { },
  inputProps = {},
  disabled = false,
  readOnly = false,
  isMultiline = false,
  minRows = 1,
  maxRows = 3,
  txtFldStyle = {},
  description = "",
  infoname,
}) => {
  const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
  const emailRegex = /^[^\s@]+@[^\s@.]+\.[^\s@.]+$/;
  const [textCountValidation, setTextCountValidation] = useState(null);
  const validateInput = (val) => {
    const trimmed = val.trim();

    switch (fldType) {
      case "email":
        return emailRegex.test(trimmed) ? "" : "Invalid email address";
      case "textR":
        if (!regexMin3.test(trimmed)) return "Invalid format";
        break;
      default:
        return "";
    }
  };

  const VALIDATORS = {
    text1: (trimmed) => (trimmed.length < 1 ? "1" : ""),

    text: (trimmed) => (trimmed.length < 3 ? "3" : ""),

    textR: (trimmed) => {
      if (trimmed.length < 3) return "3";
      if (!regexMin3.test(trimmed)) return "Invalid Format";
      return "";
    },

    textO: (trimmed) => (trimmed.length > 0 && trimmed.length < 3 ? "3" : ""),

    text4: (trimmed) => (trimmed.length < 4 ? "4" : ""),

    text5: (trimmed) => (trimmed.length < 5 ? "5" : ""),
  };

  const validateTextCount = (val) => {
    const trimmed = val.trim();
    const validator = VALIDATORS[fldType];

    return validator ? validator(trimmed) : "";
  };

  const handleChange = (e) => {
    let raw = e.target.value;
    if (type === "number") {
      const numericValue = Number(raw) || "";
      setValue(numericValue);
      setValueError("");
      setTextCountValidation("");
      return;
    }
    let newValue = e.target.value.trimStart();
    if (/\s$/.test(newValue)) {
      newValue = newValue.trimEnd() + " ";
    }
    const errorMsg = validateInput(newValue);
    const textCountErrorMsg = validateTextCount(newValue);
    setValueError(errorMsg);
    setTextCountValidation(textCountErrorMsg);
    setValue(newValue);
  };

  const textFieldStyle = {
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d9d9d9", borderRadius: "0px" }, // Default
      "&:hover fieldset": { borderColor: "#8c8c8c" }, // Hover
      "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
      "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" }, // Disabled
      "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
    ...txtFldStyle,
  };

  const InputProps = {
    readOnly,
    ...inputProps,
    sx: {
      backgroundColor: readOnly ? "#f5f5f5" : "#fff",
      pr: 0,
      "&.Mui-disabled": {
        color: "#c0c0c0",
        WebkitTextFillColor: "#c0c0c0",
      },
      "& .MuiInputBase-input": {
        pr: 0,
        ...(type === "number" && {

        }),
      },

      "& .MuiInputAdornment-root": {
        m: 0,
        p: 0,
        height: "100%",
        maxHeight: "100%",
      },
    },
    endAdornment: type === "number" && !isMultiline && (
      <InputAdornment position="end" sx={{ m: 0, p: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            m: 0,
            p: 0,
          }}
        >
          <IconButton
            disableRipple
            size="small"
            sx={{
              m: 0,
              p: 0,
              width: "22px",
              height: "50%",
              borderRadius: 0,
              "&:hover": { backgroundColor: "#E5E5E5" },
            }}
            onClick={() =>
              handleChange({
                target: { value: Number(value || 0) + 1 },
              })
            }
          >
            <KeyboardArrowUpIcon sx={{ fontSize: "16px" }} />
          </IconButton>

          <IconButton
            disableRipple
            size="small"
            sx={{
              m: 0,
              p: 0,
              width: "22px",
              height: "50%",
              borderRadius: 0,
              "&:hover": { backgroundColor: "#E5E5E5" },
            }}
            onClick={() =>
              handleChange({
                target: { value: Number(value || 0) - 1 },
              })
            }
          >
            <KeyboardArrowDownIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Box>
      </InputAdornment>
    ),
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
          <Box sx={{ width: 5, height: 5 }}>
            {infoname?.length > 0 ? (
              <InfoButton
                dimension={{ width: 15, height: 15 }}
                infoKey={infoname}
                color={"black"}
              />
            ) : null}
          </Box>
        </Typography>
      )}
      {Boolean(description) && (
        <Typography
          sx={{
            mb: 0.5,
            fontSize: "0.8rem",
            fontWeight: 500,
            color: "#6f7174",
          }}
        >
          {description}
        </Typography>
      )}

      {Boolean(textCountValidation) && (
        <Typography
          sx={{
            mb: 0.5,
            fontSize: "0.8rem",
            fontWeight: 500,
            color: "#6f7174",
          }}
        >
          Minimum{" "}
          <Typography
            component="span"
            sx={{ color: "red", fontSize: "0.8rem", fontWeight: 500, mb: 0.5 }}
          >
            {textCountValidation}
          </Typography>{" "}
          character{Number(textCountValidation) > 1 ? "s" : ""} required
        </Typography>
      )}

      <TextField
        fullWidth
        size={size}
        type={type}
        variant={variant}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        error={Boolean(valueError)}
        helperText={valueError}
        multiline={isMultiline}
        minRows={minRows}
        maxRows={maxRows}
        disabled={disabled}
        InputProps={InputProps}
        sx={textFieldStyle}
      />
    </Box>
  );
};

CustomFormTextFieldV3.propTypes = {
  size: PropTypes.string,
  label: PropTypes.node,
  type: PropTypes.string,
  fldType: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func,
  variant: PropTypes.string,
  valueError: PropTypes.string,
  setValueError: PropTypes.func,
  inputProps: PropTypes.object,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isMultiline: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  txtFldStyle: PropTypes.object,
  description: PropTypes.node,
  infoname: PropTypes.string,
};

export default CustomFormTextFieldV3;
